import React, { useEffect } from 'react';
import styles from '../editModal.module.scss';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import restFactory from 'v2/utils/restFactory';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { DataProduct, DataProductCost } from 'v2/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import { Button, FormGroup } from 'react-bootstrap';
import {
  CostField,
  getCurrencyOptions,
  getTimeScaleOptions,
} from 'v2/utils/dataProductsUtils';
import Select from 'v2/form/Select/Select';
import TextCreateDropDownPairFilter from 'v2/Filters/TextCreateDropDownPairFilter/TextCreateDropDownPairFilter';
import * as yup from 'yup';
import { TranslationKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface EditCostsModalProps {
  show: boolean;
  dataProduct: DataProduct;
  onClose: (dataProduct?: DataProduct) => void;
}

interface CostsForm {
  costs: CostField[];
}

const CostFieldSchema = yup.object().shape({
  time_period: yup.string().required(),
  cost: yup.object().shape({
    unit: yup.mixed().required(),
    value: yup.string().required(),
  }),
});

const schema = yup.object().shape({
  costs: yup.array().of(CostFieldSchema),
});

const getDefaultValue = (costs: DataProductCost[] = []): CostsForm => ({
  costs: costs.map((value) => ({
    time_period: value.time_period,
    cost: { value: `${value.amount}`, unit: `${value.currency}` },
  })),
});

const EditCostsModal = ({
  show,
  dataProduct,
  onClose,
}: EditCostsModalProps) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    getValues,
    reset: resetForm,
    formState: { isValid },
  } = useForm<CostsForm>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: getDefaultValue(dataProduct.costs),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'costs',
  });

  useEffect(() => {
    resetForm(getDefaultValue(dataProduct.costs));
  }, [dataProduct.costs]);

  const { isLoading: isDataProductMutateLoading, mutate: dataProductMutate } =
    useMutation(
      async (costs: DataProductCost[]) => {
        return await restFactory.patch<DataProduct>(
          `/dj/api/v1/data_product/${dataProduct.id}/`,
          { costs }
        );
      },
      {
        onSuccess: () => {
          onClose();
          toast.success('Costs updated successfully');
        },
        onError: () => {
          toast.error(
            `Error updating ${t(TranslationKeys.dataProduct_one)} costs.`
          );
        },
      }
    );

  const closeModal = () => {
    resetForm();
    onClose();
  };

  const onSubmit = (data: CostsForm) => {
    if (isValid) {
      const body: DataProductCost[] = data.costs.map((value) => ({
        time_period: value.time_period,
        amount: parseInt(value.cost.value),
        currency: `${value.cost.unit}`,
      }));
      dataProductMutate(body);
    }
  };

  return (
    <ConfirmationModal
      show={show}
      title="Edit Costs"
      onCancel={closeModal}
      isLoading={isDataProductMutateLoading}
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      okBtnText="Save"
      centered={true}
      animation={true}
      className={styles.viewModals}
    >
      <>
        {fields.map((field, index) => (
          <FormGroup key={field.id} data-cy="dataset-cost-form-group">
            <h6 className={styles.addCosttitle}>Cost</h6>
            <div
              className={classnames(
                'd-flex',
                'flex-row justify-content-between'
              )}
            >
              <TextCreateDropDownPairFilter
                name={`costs.${index}.cost`}
                control={control}
                type="number"
                placeholder="0"
                prefix="$"
                options={getCurrencyOptions()}
                defaultValue={getValues(`costs.${index}.cost`)}
              />
              <div
                className="ml-3 flex-grow-1"
                data-cy="time-period-dropdown-container"
              >
                <Select
                  register={register(`costs.${index}.time_period`, {
                    required: true,
                  })}
                  required={true}
                  hasValue={!!getValues(`costs.${index}.time_period`)}
                  options={getTimeScaleOptions()}
                />
              </div>
              <Button
                variant="link"
                onClick={() => {
                  remove(index);
                }}
              >
                Delete
              </Button>
            </div>
          </FormGroup>
        ))}
        {fields.length < 20 && (
          <Button
            variant="link"
            onClick={() => append({ cost: { unit: 'USD', value: '' } })}
            className={styles.addCost}
            disabled={!isValid}
          >
            + Add another cost
          </Button>
        )}
      </>
    </ConfirmationModal>
  );
};

export default EditCostsModal;
